import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { API } from "@/lib/api";
import { VideoBeforeAfter, VideoCard } from "./VideoBeforeAfter";
import { ShareButtons } from "./ShareButtons";

const PAIRS = [
  { before: "/job-a.webp", after: "/job-d.webp", titleKey: "gal_proj1" },
  { before: "/job-b.webp", after: "/job-c.webp", titleKey: "gal_proj2" },
  { before: "/job-j.webp", after: "/job-k.webp", titleKey: "gal_proj3", focus: "50% 22%", initial: 72 },
  { before: "/job-l.webp", after: "/job-m.webp", titleKey: "gal_proj4" },
];

const BeforeAfter = ({ before, after, title, idx, focus = "50% 50%", initial = 50 }) => {
  const { t } = useLang();
  const [pos, setPos] = useState(initial);
  return (
    <motion.figure
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: idx * 0.15 }}
    >
      <div
        className="relative overflow-hidden rounded-3xl border border-[#E2E8F0] shadow-2xl h-[360px] sm:h-[440px] select-none"
        data-testid={idx === 0 ? "before-after-slider" : `before-after-slider-${idx}`}
      >
        <img
          src={after}
          alt={`${title} — after PERETE installation`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: focus }}
        />
        <img
          src={before}
          alt={`${title} — before PERETE installation`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: focus, clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />
        <span className="absolute top-4 left-4 rounded-full bg-[#0E1A2B]/85 backdrop-blur-md px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-white">
          {t("gal_before")}
        </span>
        <span className="absolute top-4 right-4 rounded-full bg-[#F26522] px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-white">
          {t("gal_after")}
        </span>
        <div
          className="absolute inset-y-0 w-1 bg-white shadow-[0_0_20px_rgba(0,0,0,0.5)] pointer-events-none"
          style={{ left: `${pos}%` }}
        >
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-[#F26522] text-white shadow-xl">
            <MoveHorizontal className="h-5 w-5" />
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={pos}
          onChange={(e) => setPos(Number(e.target.value))}
          aria-label={t("gal_drag")}
          className="absolute inset-0 h-full w-full opacity-0 cursor-ew-resize"
          data-testid={idx === 0 ? "ba-range" : `ba-range-${idx}`}
        />
      </div>
      <figcaption className="mt-4 flex items-center gap-3">
        <span className="h-1.5 w-1.5 rounded-full bg-[#F26522]" />
        <p className="font-display font-bold text-[#0E1A2B]">{title}</p>
      </figcaption>
    </motion.figure>
  );
};

export default function Gallery() {
  const { t } = useLang();
  const [uploaded, setUploaded] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setUploaded)
      .catch(() => {});
    fetch(`${API}/gallery/videos`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setVideos)
      .catch(() => {});
  }, []);

  return (
    <section
      id="gallery"
      className="py-16 sm:py-24 lg:py-32 bg-[#FAF8F5]"
      data-testid="gallery-section"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-14"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F26522] font-semibold mb-4">
            {t("gal_eyebrow")}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
            {t("gal_h")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#475569]">
            {t("gal_sub")}
          </p>
          <div className="mt-6">
            <ShareButtons />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {PAIRS.map((p, idx) => (
            <BeforeAfter
              key={p.titleKey}
              before={p.before}
              after={p.after}
              title={t(p.titleKey)}
              focus={p.focus}
              initial={p.initial}
              idx={idx}
            />
          ))}
        </div>

        <VideoBeforeAfter />

        {videos.length > 0 && (
          <div className="mt-10" data-testid="gallery-videos">
            <h3 className="mb-6 font-display text-xl sm:text-2xl font-bold text-[#0E1A2B]">
              {t("gal_more_videos")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
              {videos.map((v) => (
                <figure key={v.id} data-testid={`gallery-video-${v.id}`}>
                  <VideoCard
                    src={`${API}/files/${v.storage_path}`}
                    poster={v.poster_path ? `${API}/files/${v.poster_path}` : undefined}
                    label=""
                    testId={`gallery-video-card-${v.id}`}
                  />
                  {v.caption && (
                    <figcaption className="mt-3 font-display font-bold text-sm text-[#0E1A2B]">
                      {v.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        )}

        <motion.h3
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-16 mb-8 font-display text-xl sm:text-2xl font-bold text-[#0E1A2B]"
          data-testid="gallery-more-heading"
        >
          {t("gal_more")}
        </motion.h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {[
            { img: "/job-i.webp", key: "gal_p1" },
            { img: "/job-h.webp", key: "gal_p2" },
            { img: "/job-g.webp", key: "gal_p3" },
            { img: "/job-e.webp", key: "gal_p4" },
            { img: "/job-f.webp", key: "gal_p5" },
          ].map((p, idx) => (
            <motion.figure
              key={p.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (idx % 3) * 0.1 }}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-sm hover:shadow-xl transition-shadow duration-300"
              data-testid={`gallery-card-${idx}`}
            >
              <div className="h-52 sm:h-64 overflow-hidden">
                <img
                  src={p.img}
                  alt={t(p.key)}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <figcaption className="p-4">
                <p className="font-display font-bold text-sm text-[#0E1A2B]">
                  {t(p.key)}
                </p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {uploaded.length > 0 && (
          <div
            className="mt-6 lg:mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
            data-testid="gallery-uploaded"
          >
            {uploaded.map((p, idx) => (
              <motion.figure
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (idx % 3) * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-sm hover:shadow-xl transition-shadow duration-300"
                data-testid={`gallery-uploaded-${p.id}`}
              >
                <div className="h-52 sm:h-64 overflow-hidden">
                  <img
                    src={`${API}/files/${p.storage_path}`}
                    alt={p.caption || "PERETE flooring installation work"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                {p.caption && (
                  <figcaption className="p-4">
                    <p className="font-display font-bold text-sm text-[#0E1A2B]">
                      {p.caption}
                    </p>
                  </figcaption>
                )}
              </motion.figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
